import React, { useEffect, useState, useCallback } from 'react';
import { Button, Card, Tag, Input, Space, Popconfirm, message, Select, Tooltip, Badge, Row, Col } from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    BellOutlined,
    ApiOutlined,
    ThunderboltOutlined,
    ExperimentOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import { modelApi } from '../../services/api';
import { ModelConfigItem } from '../../services/types';
import ModelFormModal from '../../components/admin/ModelFormModal';
import { useAuthStore } from '../../stores/authStore';

const providerColors: Record<string, string> = {
    openai: '#10a37f',
    deepseek: '#4f6ef7',
    siliconflow: '#6366f1',
    local: '#f59e0b',
    custom: '#8b5cf6',
};

const ModelConfigPage: React.FC = () => {
    const { user } = useAuthStore();
    const [items, setItems] = useState<ModelConfigItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [typeFilter, setTypeFilter] = useState<string | undefined>();
    const [modalOpen, setModalOpen] = useState(false);
    const [editData, setEditData] = useState<ModelConfigItem | undefined>();
    const [testingId, setTestingId] = useState<number | null>(null);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await modelApi.list({
                skip: 0,
                limit: 100,
                model_type: typeFilter,
                keyword: keyword || undefined,
            });
            if (res.success && res.data) {
                setItems(res.data.items || []);
            }
        } catch {
            message.error('获取模型配置失败');
        } finally {
            setLoading(false);
        }
    }, [keyword, typeFilter]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const handleDelete = async (id: number) => {
        try {
            await modelApi.delete(id);
            message.success('删除成功');
            fetchList();
        } catch {
            message.error('删除失败');
        }
    };

    const handleTest = async (id: number) => {
        setTestingId(id);
        try {
            const res = await modelApi.test(id);
            if (res.success) {
                const data = res.data;
                message.success(`连接成功！响应 ${data.response_time_ms}ms${data.vector_dim ? `, 维度 ${data.vector_dim}` : ''}`);
            } else {
                message.error(res.message || '连接失败');
            }
        } catch {
            message.error('连接测试异常');
        } finally {
            setTestingId(null);
        }
    };

    const renderCard = (item: ModelConfigItem) => {
        const isLLM = item.model_type === 'llm';
        const providerColor = providerColors[item.provider] || '#6b7280';

        return (
            <Col xs={24} sm={12} lg={8} key={item.id}>
                <Card
                    hoverable
                    style={{ borderRadius: 12, height: '100%' }}
                    styles={{ body: { padding: 20 } }}
                    actions={[
                        <Tooltip title="测试连接" key="test">
                            <Button
                                type="text"
                                size="small"
                                icon={testingId === item.id ? <LoadingOutlined /> : <ApiOutlined />}
                                onClick={() => handleTest(item.id)}
                                disabled={testingId === item.id}
                            />
                        </Tooltip>,
                        <Tooltip title="编辑" key="edit">
                            <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => { setEditData(item); setModalOpen(true); }}
                            />
                        </Tooltip>,
                        <Popconfirm title="确定删除此模型配置？" onConfirm={() => handleDelete(item.id)} okText="删除" cancelText="取消" key="del">
                            <Tooltip title="删除">
                                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                        </Popconfirm>,
                    ]}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <Space>
                            <div style={{
                                width: 36, height: 36, borderRadius: 8,
                                background: isLLM ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18,
                            }}>
                                {isLLM ? <ThunderboltOutlined /> : <ExperimentOutlined />}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: 15 }}>{item.name}</div>
                                <div style={{ color: '#71717a', fontSize: 12 }}>{item.model_name}</div>
                            </div>
                        </Space>
                        <Badge status={item.is_active ? 'success' : 'default'} text={item.is_active ? '启用' : '禁用'} />
                    </div>

                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        <Tag color={isLLM ? 'blue' : 'magenta'}>{isLLM ? 'LLM' : 'Embedding'}</Tag>
                        <Tag style={{ color: providerColor, borderColor: providerColor }}>{item.provider}</Tag>
                    </div>

                    {isLLM && (
                        <div style={{ color: '#71717a', fontSize: 12 }}>
                            Max Tokens: {item.max_tokens || '-'} · Temp: {item.temperature ?? '-'} · Top-P: {item.top_p ?? '-'}
                        </div>
                    )}
                    {!isLLM && (
                        <div style={{ color: '#71717a', fontSize: 12 }}>
                            维度: {item.vector_size || '-'} · 最大长度: {item.max_length || '-'}
                        </div>
                    )}

                    {item.description && (
                        <div style={{ color: '#a1a1aa', fontSize: 12, marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.description}
                        </div>
                    )}
                </Card>
            </Col>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* 顶部栏 */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0 32px', height: 64, background: '#fff', borderBottom: '1px solid #e4e4e7', flexShrink: 0,
            }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#18181b' }}>模型配置</h2>
                <Space>
                    <BellOutlined style={{ fontSize: 18, color: '#71717a', cursor: 'pointer' }} />
                    <div style={{
                        width: 32, height: 32, borderRadius: '50%', background: '#3b82f6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600, fontSize: 14,
                    }}>
                        {user?.username?.[0]?.toUpperCase() || 'A'}
                    </div>
                </Space>
            </div>

            {/* 内容区 */}
            <div style={{ flex: 1, padding: 24, overflow: 'auto' }}>
                {/* 操作栏 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <Space>
                        <Input
                            placeholder="搜索模型..."
                            prefix={<SearchOutlined style={{ color: '#a1a1aa' }} />}
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            onPressEnter={fetchList}
                            style={{ width: 280 }}
                            allowClear
                        />
                        <Select
                            placeholder="类型筛选"
                            value={typeFilter}
                            onChange={setTypeFilter}
                            allowClear
                            style={{ width: 150 }}
                            options={[
                                { label: 'LLM 大模型', value: 'llm' },
                                { label: 'Embedding 嵌入', value: 'embedding' },
                            ]}
                        />
                    </Space>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditData(undefined); setModalOpen(true); }}>
                        添加模型
                    </Button>
                </div>

                {/* 卡片列表 */}
                <Row gutter={[16, 16]}>
                    {loading ? (
                        <Col span={24}><div style={{ textAlign: 'center', padding: 60, color: '#a1a1aa' }}>加载中...</div></Col>
                    ) : items.length === 0 ? (
                        <Col span={24}><div style={{ textAlign: 'center', padding: 60, color: '#a1a1aa' }}>暂无模型配置，点击&quot;添加模型&quot;开始</div></Col>
                    ) : (
                        items.map(renderCard)
                    )}
                </Row>
            </div>

            <ModelFormModal
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onSuccess={() => { setModalOpen(false); fetchList(); }}
                editData={editData}
            />
        </div>
    );
};

export default ModelConfigPage;
